/**
 * TIMEZONE HANDLING UTILITIES
 * 
 * Rules:
 * 1. Backend provides UTC timestamps + timezone string (IANA format)
 * 2. Frontend converts UTC → local time on the fly
 * 3. Never assume browser timezone unless explicitly intended
 * 4. Always label timezone in UI
 * 5. Do not cache converted values - recompute on every render
 */

import { toZonedTime, format, formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';

// Comprehensive list of world timezones (IANA timezone database)
const WORLD_TIMEZONES = [
  'Africa/Abidjan', 'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Algiers', 'Africa/Asmara',
  'Africa/Bamako', 'Africa/Bangui', 'Africa/Banjul', 'Africa/Bissau', 'Africa/Blantyre',
  'Africa/Brazzaville', 'Africa/Bujumbura', 'Africa/Cairo', 'Africa/Casablanca', 'Africa/Ceuta',
  'Africa/Conakry', 'Africa/Dakar', 'Africa/Dar_es_Salaam', 'Africa/Djibouti', 'Africa/Douala',
  'Africa/El_Aaiun', 'Africa/Freetown', 'Africa/Gaborone', 'Africa/Harare', 'Africa/Johannesburg',
  'Africa/Juba', 'Africa/Kampala', 'Africa/Khartoum', 'Africa/Kigali', 'Africa/Kinshasa',
  'Africa/Lagos', 'Africa/Libreville', 'Africa/Lome', 'Africa/Luanda', 'Africa/Lubumbashi',
  'Africa/Lusaka', 'Africa/Malabo', 'Africa/Maputo', 'Africa/Maseru', 'Africa/Mbabane',
  'Africa/Mogadishu', 'Africa/Monrovia', 'Africa/Nairobi', 'Africa/Ndjamena', 'Africa/Niamey',
  'Africa/Nouakchott', 'Africa/Ouagadougou', 'Africa/Porto-Novo', 'Africa/Sao_Tome', 'Africa/Tripoli',
  'Africa/Tunis', 'Africa/Windhoek',
  'America/Adak', 'America/Anchorage', 'America/Anguilla', 'America/Antigua', 'America/Araguaina',
  'America/Argentina/Buenos_Aires', 'America/Argentina/Catamarca', 'America/Argentina/Cordoba',
  'America/Argentina/Jujuy', 'America/Argentina/La_Rioja', 'America/Argentina/Mendoza',
  'America/Argentina/Rio_Gallegos', 'America/Argentina/Salta', 'America/Argentina/San_Juan',
  'America/Argentina/San_Luis', 'America/Argentina/Tucuman', 'America/Argentina/Ushuaia',
  'America/Aruba', 'America/Asuncion', 'America/Atikokan', 'America/Bahia', 'America/Bahia_Banderas',
  'America/Barbados', 'America/Belem', 'America/Belize', 'America/Blanc-Sablon', 'America/Boa_Vista',
  'America/Bogota', 'America/Boise', 'America/Cambridge_Bay', 'America/Campo_Grande', 'America/Cancun',
  'America/Caracas', 'America/Cayenne', 'America/Cayman', 'America/Chicago', 'America/Chihuahua',
  'America/Costa_Rica', 'America/Creston', 'America/Cuiaba', 'America/Curacao', 'America/Danmarkshavn',
  'America/Dawson', 'America/Dawson_Creek', 'America/Denver', 'America/Detroit', 'America/Dominica',
  'America/Edmonton', 'America/Eirunepe', 'America/El_Salvador', 'America/Fort_Nelson', 'America/Fortaleza',
  'America/Glace_Bay', 'America/Godthab', 'America/Goose_Bay', 'America/Grand_Turk', 'America/Grenada',
  'America/Guadeloupe', 'America/Guatemala', 'America/Guayaquil', 'America/Guyana', 'America/Halifax',
  'America/Havana', 'America/Hermosillo', 'America/Indiana/Indianapolis', 'America/Indiana/Knox',
  'America/Indiana/Marengo', 'America/Indiana/Petersburg', 'America/Indiana/Tell_City', 'America/Indiana/Vevay',
  'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Inuvik', 'America/Iqaluit',
  'America/Jamaica', 'America/Juneau', 'America/Kentucky/Louisville', 'America/Kentucky/Monticello',
  'America/Kralendijk', 'America/La_Paz', 'America/Lima', 'America/Los_Angeles', 'America/Lower_Princes',
  'America/Maceio', 'America/Managua', 'America/Manaus', 'America/Marigot', 'America/Martinique',
  'America/Matamoros', 'America/Mazatlan', 'America/Menominee', 'America/Merida', 'America/Metlakatla',
  'America/Mexico_City', 'America/Miquelon', 'America/Moncton', 'America/Monterrey', 'America/Montevideo',
  'America/Montreal', 'America/Montserrat', 'America/Nassau', 'America/New_York', 'America/Nipigon',
  'America/Nome', 'America/Noronha', 'America/North_Dakota/Beulah', 'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem', 'America/Ojinaga', 'America/Panama', 'America/Pangnirtung',
  'America/Paramaribo', 'America/Phoenix', 'America/Port-au-Prince', 'America/Port_of_Spain',
  'America/Porto_Velho', 'America/Puerto_Rico', 'America/Punta_Arenas', 'America/Rainy_River',
  'America/Rankin_Inlet', 'America/Recife', 'America/Regina', 'America/Resolute', 'America/Rio_Branco',
  'America/Santarem', 'America/Santiago', 'America/Santo_Domingo', 'America/Sao_Paulo', 'America/Scoresbysund',
  'America/Sitka', 'America/St_Barthelemy', 'America/St_Johns', 'America/St_Kitts', 'America/St_Lucia',
  'America/St_Thomas', 'America/St_Vincent', 'America/Swift_Current', 'America/Tegucigalpa', 'America/Thule',
  'America/Thunder_Bay', 'America/Tijuana', 'America/Toronto', 'America/Tortola', 'America/Vancouver',
  'America/Whitehorse', 'America/Winnipeg', 'America/Yakutat', 'America/Yellowknife',
  'Asia/Aden', 'Asia/Almaty', 'Asia/Amman', 'Asia/Anadyr', 'Asia/Aqtau', 'Asia/Aqtobe', 'Asia/Ashgabat',
  'Asia/Atyrau', 'Asia/Baghdad', 'Asia/Bahrain', 'Asia/Baku', 'Asia/Bangkok', 'Asia/Barnaul', 'Asia/Beirut',
  'Asia/Bishkek', 'Asia/Brunei', 'Asia/Chita', 'Asia/Choibalsan', 'Asia/Colombo', 'Asia/Damascus',
  'Asia/Dhaka', 'Asia/Dili', 'Asia/Dubai', 'Asia/Dushanbe', 'Asia/Famagusta', 'Asia/Gaza', 'Asia/Hebron',
  'Asia/Ho_Chi_Minh', 'Asia/Hong_Kong', 'Asia/Hovd', 'Asia/Irkutsk', 'Asia/Jakarta', 'Asia/Jayapura',
  'Asia/Jerusalem', 'Asia/Kabul', 'Asia/Kamchatka', 'Asia/Karachi', 'Asia/Kathmandu', 'Asia/Khandyga',
  'Asia/Kolkata', 'Asia/Krasnoyarsk', 'Asia/Kuala_Lumpur', 'Asia/Kuching', 'Asia/Kuwait', 'Asia/Macau',
  'Asia/Magadan', 'Asia/Makassar', 'Asia/Manila', 'Asia/Muscat', 'Asia/Nicosia', 'Asia/Novokuznetsk',
  'Asia/Novosibirsk', 'Asia/Omsk', 'Asia/Oral', 'Asia/Phnom_Penh', 'Asia/Pontianak', 'Asia/Pyongyang',
  'Asia/Qatar', 'Asia/Qostanay', 'Asia/Qyzylorda', 'Asia/Riyadh', 'Asia/Sakhalin', 'Asia/Samarkand',
  'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Srednekolymsk', 'Asia/Taipei', 'Asia/Tashkent',
  'Asia/Tbilisi', 'Asia/Tehran', 'Asia/Thimphu', 'Asia/Tokyo', 'Asia/Tomsk', 'Asia/Ulaanbaatar',
  'Asia/Urumqi', 'Asia/Ust-Nera', 'Asia/Vientiane', 'Asia/Vladivostok', 'Asia/Yakutsk', 'Asia/Yangon',
  'Asia/Yekaterinburg', 'Asia/Yerevan',
  'Atlantic/Azores', 'Atlantic/Bermuda', 'Atlantic/Canary', 'Atlantic/Cape_Verde', 'Atlantic/Faroe',
  'Atlantic/Madeira', 'Atlantic/Reykjavik', 'Atlantic/South_Georgia', 'Atlantic/St_Helena', 'Atlantic/Stanley',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Broken_Hill', 'Australia/Currie', 'Australia/Darwin',
  'Australia/Eucla', 'Australia/Hobart', 'Australia/Lindeman', 'Australia/Lord_Howe', 'Australia/Melbourne',
  'Australia/Perth', 'Australia/Sydney',
  'Europe/Amsterdam', 'Europe/Andorra', 'Europe/Astrakhan', 'Europe/Athens', 'Europe/Belgrade',
  'Europe/Berlin', 'Europe/Bratislava', 'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest',
  'Europe/Busingen', 'Europe/Chisinau', 'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Gibraltar',
  'Europe/Guernsey', 'Europe/Helsinki', 'Europe/Isle_of_Man', 'Europe/Istanbul', 'Europe/Jersey',
  'Europe/Kaliningrad', 'Europe/Kiev', 'Europe/Kirov', 'Europe/Lisbon', 'Europe/Ljubljana',
  'Europe/London', 'Europe/Luxembourg', 'Europe/Madrid', 'Europe/Malta', 'Europe/Mariehamn',
  'Europe/Minsk', 'Europe/Monaco', 'Europe/Moscow', 'Europe/Oslo', 'Europe/Paris', 'Europe/Podgorica',
  'Europe/Prague', 'Europe/Riga', 'Europe/Rome', 'Europe/Samara', 'Europe/San_Marino', 'Europe/Sarajevo',
  'Europe/Saratov', 'Europe/Simferopol', 'Europe/Skopje', 'Europe/Sofia', 'Europe/Stockholm',
  'Europe/Tallinn', 'Europe/Tirane', 'Europe/Ulyanovsk', 'Europe/Uzhgorod', 'Europe/Vaduz',
  'Europe/Vatican', 'Europe/Vienna', 'Europe/Vilnius', 'Europe/Volgograd', 'Europe/Warsaw',
  'Europe/Zagreb', 'Europe/Zaporozhye', 'Europe/Zurich',
  'Indian/Antananarivo', 'Indian/Chagos', 'Indian/Christmas', 'Indian/Cocos', 'Indian/Comoro',
  'Indian/Kerguelen', 'Indian/Mahe', 'Indian/Maldives', 'Indian/Mauritius', 'Indian/Mayotte',
  'Indian/Reunion',
  'Pacific/Apia', 'Pacific/Auckland', 'Pacific/Bougainville', 'Pacific/Chatham', 'Pacific/Chuuk',
  'Pacific/Easter', 'Pacific/Efate', 'Pacific/Enderbury', 'Pacific/Fakaofo', 'Pacific/Fiji',
  'Pacific/Funafuti', 'Pacific/Galapagos', 'Pacific/Gambier', 'Pacific/Guadalcanal', 'Pacific/Guam',
  'Pacific/Honolulu', 'Pacific/Kiritimati', 'Pacific/Kosrae', 'Pacific/Kwajalein', 'Pacific/Majuro',
  'Pacific/Marquesas', 'Pacific/Midway', 'Pacific/Nauru', 'Pacific/Niue', 'Pacific/Norfolk',
  'Pacific/Noumea', 'Pacific/Pago_Pago', 'Pacific/Palau', 'Pacific/Pitcairn', 'Pacific/Pohnpei',
  'Pacific/Port_Moresby', 'Pacific/Rarotonga', 'Pacific/Saipan', 'Pacific/Tahiti', 'Pacific/Tarawa',
  'Pacific/Tongatapu', 'Pacific/Wake', 'Pacific/Wallis',
  'UTC'
];

// Get ALL available timezones
export const getAllTimezones = (): string[] => {
  return WORLD_TIMEZONES;
};

// Format timezone for display with offset
export const getTimezoneDisplay = (timezone: string): { label: string; offset: string } => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(now);
    const tzName = parts.find(part => part.type === 'timeZoneName')?.value || '';
    
    // Get offset
    const offset = getTimezoneOffset(timezone);
    
    // Format: "Singapore - SGT (UTC+8)"
    const city = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    return {
      label: `${city} - ${tzName}`,
      offset: offset
    };
  } catch {
    return {
      label: timezone,
      offset: ''
    };
  }
};

// Get timezone offset (e.g., "UTC+8", "UTC-5")
export const getTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date();
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
    
    if (offset === 0) return 'UTC';
    const sign = offset > 0 ? '+' : '';
    return `UTC${sign}${offset}`;
  } catch {
    return '';
  }
};

// Legacy - keep for backward compatibility
export const TIMEZONES = [
  { value: 'Asia/Singapore', label: 'Singapore (SGT, UTC+8)', offset: '+8' },
  { value: 'Asia/Yangon', label: 'Myanmar (MMT, UTC+6:30)', offset: '+6:30' },
  { value: 'Asia/Tokyo', label: 'Japan (JST, UTC+9)', offset: '+9' },
  { value: 'Asia/Bangkok', label: 'Thailand (ICT, UTC+7)', offset: '+7' },
  { value: 'America/New_York', label: 'New York (EST/EDT)', offset: '-5/-4' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)', offset: '-8/-7' },
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: '+0/+1' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)', offset: '+11' },
];

export const getTimezoneAbbr = (timezone: string): string => {
  const abbrs: Record<string, string> = {
    'Asia/Singapore': 'SGT',
    'Asia/Yangon': 'MMT',
    'Asia/Tokyo': 'JST',
    'Asia/Bangkok': 'ICT',
    'America/New_York': 'EST/EDT',
    'America/Los_Angeles': 'PST/PDT',
    'Europe/London': 'GMT/BST',
    'Australia/Sydney': 'AEDT',
  };
  return abbrs[timezone] || timezone;
};

export const convertToTimezone = (dateString: string, timezone: string): Date => {
  // Normalize timestamp format
  let normalizedTimestamp = dateString.trim();
  
  // Replace space with T
  if (normalizedTimestamp.includes(' ') && !normalizedTimestamp.includes('T')) {
    const parts = normalizedTimestamp.split(' ');
    if (parts.length >= 2) {
      normalizedTimestamp = parts[0] + 'T' + parts.slice(1).join(' ');
    }
  }
  
  // Remove trailing milliseconds if no Z
  if (!normalizedTimestamp.endsWith('Z') && normalizedTimestamp.includes('.')) {
    const dotIndex = normalizedTimestamp.lastIndexOf('.');
    const afterDot = normalizedTimestamp.substring(dotIndex + 1);
    if (/^\d+$/.test(afterDot)) {
      normalizedTimestamp = normalizedTimestamp.substring(0, dotIndex);
    }
  }
  
  // Add Z suffix if missing
  if (!normalizedTimestamp.endsWith('Z') && 
      !normalizedTimestamp.includes('+') && 
      !normalizedTimestamp.includes('-', 10)) {
    normalizedTimestamp = normalizedTimestamp + 'Z';
  }
  
  return toZonedTime(new Date(normalizedTimestamp), timezone);
};

export const formatInTimezone = (
  dateString: string,
  timezone: string,
  formatStr: string = 'PPpp'
): string => {
  // Normalize timestamp format
  let normalizedTimestamp = dateString.trim();
  
  // Replace space with T
  if (normalizedTimestamp.includes(' ') && !normalizedTimestamp.includes('T')) {
    const parts = normalizedTimestamp.split(' ');
    if (parts.length >= 2) {
      normalizedTimestamp = parts[0] + 'T' + parts.slice(1).join(' ');
    }
  }
  
  // Remove trailing milliseconds if no Z
  if (!normalizedTimestamp.endsWith('Z') && normalizedTimestamp.includes('.')) {
    const dotIndex = normalizedTimestamp.lastIndexOf('.');
    const afterDot = normalizedTimestamp.substring(dotIndex + 1);
    if (/^\d+$/.test(afterDot)) {
      normalizedTimestamp = normalizedTimestamp.substring(0, dotIndex);
    }
  }
  
  // Add Z suffix if missing
  if (!normalizedTimestamp.endsWith('Z') && 
      !normalizedTimestamp.includes('+') && 
      !normalizedTimestamp.includes('-', 10)) {
    normalizedTimestamp = normalizedTimestamp + 'Z';
  }
  
  const zonedDate = convertToTimezone(normalizedTimestamp, timezone);
  return format(zonedDate, formatStr, { timeZone: timezone });
};

export const formatTimeWithTimezone = (
  dateString: string,
  timezone: string
): string => {
  // Normalize timestamp format (handle "2026-02-21 17:00:00.000" from database)
  let normalizedTimestamp = dateString.trim();
  
  // Replace space with T
  if (normalizedTimestamp.includes(' ') && !normalizedTimestamp.includes('T')) {
    const parts = normalizedTimestamp.split(' ');
    if (parts.length >= 2) {
      normalizedTimestamp = parts[0] + 'T' + parts.slice(1).join(' ');
    }
  }
  
  // Remove trailing milliseconds if no Z
  if (!normalizedTimestamp.endsWith('Z') && normalizedTimestamp.includes('.')) {
    const dotIndex = normalizedTimestamp.lastIndexOf('.');
    const afterDot = normalizedTimestamp.substring(dotIndex + 1);
    if (/^\d+$/.test(afterDot)) {
      normalizedTimestamp = normalizedTimestamp.substring(0, dotIndex);
    }
  }
  
  // Add Z suffix if missing
  if (!normalizedTimestamp.endsWith('Z') && 
      !normalizedTimestamp.includes('+') && 
      !normalizedTimestamp.includes('-', 10)) {
    normalizedTimestamp = normalizedTimestamp + 'Z';
  }
  
  const formatted = formatInTimezone(normalizedTimestamp, timezone, 'MMM dd, yyyy h:mm a');
  const abbr = getTimezoneAbbr(timezone);
  return `${formatted} ${abbr}`;
};

/**
 * Format meeting time with large emphasis (for display in UI)
 * Returns object with date, time, and timezone for flexible rendering
 * NEVER cache this - recompute on every render
 * 
 * CRITICAL: This function expects UTC timestamps from backend
 * Backend MUST send ISO 8601 format: "2026-02-22T04:45:00Z" or "2026-02-22T04:45:00.000Z"
 */
export const formatMeetingTime = (
  utcTimestamp: string,
  timezone: string
): { date: string; time: string; timezone: string; full: string; dayOfWeek: string } => {
  try {
    console.log('🕐 formatMeetingTime called:', {
      input: utcTimestamp,
      targetTimezone: timezone,
      isUTC: utcTimestamp.endsWith('Z') || utcTimestamp.includes('+00:00')
    });
    
    // Normalize the timestamp format
    // Backend might send: 
    // - "2026-02-21 17:00:00" (space, no Z)
    // - "2026-02-21 17:00:00.000" (space, milliseconds, no Z)
    // We need: "2026-02-21T17:00:00Z" (T separator, Z suffix)
    let normalizedTimestamp = utcTimestamp.trim();
    
    // Replace space with T if needed (before time part)
    if (normalizedTimestamp.includes(' ') && !normalizedTimestamp.includes('T')) {
      // Find the space between date and time (after YYYY-MM-DD)
      const parts = normalizedTimestamp.split(' ');
      if (parts.length >= 2) {
        normalizedTimestamp = parts[0] + 'T' + parts.slice(1).join(' ');
        console.log('📝 Replaced space with T:', normalizedTimestamp);
      }
    }
    
    // Remove trailing milliseconds if present and no Z (e.g., "17:00:00.000" → "17:00:00")
    // But keep them if Z is present (e.g., "17:00:00.000Z" stays as is)
    if (!normalizedTimestamp.endsWith('Z') && normalizedTimestamp.includes('.')) {
      const dotIndex = normalizedTimestamp.lastIndexOf('.');
      const afterDot = normalizedTimestamp.substring(dotIndex + 1);
      // Check if what's after the dot is just numbers (milliseconds)
      if (/^\d+$/.test(afterDot)) {
        normalizedTimestamp = normalizedTimestamp.substring(0, dotIndex);
        console.log('📝 Removed milliseconds:', normalizedTimestamp);
      }
    }
    
    // Add Z suffix if missing (and no other timezone indicator)
    if (!normalizedTimestamp.endsWith('Z') && 
        !normalizedTimestamp.includes('+') && 
        !normalizedTimestamp.includes('-', 10)) { // Check for timezone offset after date
      normalizedTimestamp = normalizedTimestamp + 'Z';
      console.log('📝 Added Z suffix:', normalizedTimestamp);
    }
    
    console.log('✅ Normalized timestamp:', normalizedTimestamp);
    
    // Parse the UTC timestamp
    const date = parseISO(normalizedTimestamp);
    
    // Validate the parsed date
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${utcTimestamp}`);
    }
    
    // Format date: "Jan 18, 2026"
    const formattedDate = formatInTimeZone(date, timezone, 'MMM dd, yyyy');
    
    // Format time: "10:30 AM"
    const formattedTime = formatInTimeZone(date, timezone, 'h:mm a');
    
    // Format day of week: "Sunday"
    const dayOfWeek = formatInTimeZone(date, timezone, 'EEEE');
    
    // Get timezone label with city name
    const tzCity = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    
    // Get timezone abbreviation
    const tzAbbr = getTimezoneAbbr(timezone);
    
    console.log('✅ formatMeetingTime result:', {
      utcInput: utcTimestamp,
      timezone: timezone,
      formattedDate,
      formattedTime,
      dayOfWeek,
      tzCity,
      tzAbbr
    });
    
    return {
      date: formattedDate,
      time: formattedTime,
      timezone: `${tzCity} time`,
      full: `${formattedDate} at ${formattedTime} (${tzCity} time)`,
      dayOfWeek: dayOfWeek
    };
  } catch (error) {
    console.error('❌ Error formatting meeting time:', {
      error,
      utcTimestamp,
      timezone
    });
    return {
      date: 'Invalid date',
      time: 'Invalid time',
      timezone: timezone,
      full: 'Invalid date/time',
      dayOfWeek: 'Unknown'
    };
  }
};

/**
 * Format time with timezone label (compact version)
 * Example: "10:30 AM (Singapore time)"
 * NEVER cache this - recompute on every render
 */
export const formatTimeWithLabel = (
  utcTimestamp: string,
  timezone: string
): string => {
  try {
    const date = parseISO(utcTimestamp);
    const formattedTime = formatInTimeZone(date, timezone, 'h:mm a');
    const tzCity = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    return `${formattedTime} (${tzCity} time)`;
  } catch (error) {
    console.error('Error formatting time with label:', error);
    return 'Invalid time';
  }
};

/**
 * Format date and time range with timezone
 * Example: "Jan 18, 2026 • 10:30 AM - 11:00 AM (Singapore time)"
 */
export const formatTimeRange = (
  utcStartTimestamp: string,
  utcEndTimestamp: string,
  timezone: string
): string => {
  try {
    const startDate = parseISO(utcStartTimestamp);
    const endDate = parseISO(utcEndTimestamp);
    
    const formattedDate = formatInTimeZone(startDate, timezone, 'MMM dd, yyyy');
    const formattedStartTime = formatInTimeZone(startDate, timezone, 'h:mm a');
    const formattedEndTime = formatInTimeZone(endDate, timezone, 'h:mm a');
    const tzCity = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    
    return `${formattedDate} • ${formattedStartTime} - ${formattedEndTime} (${tzCity} time)`;
  } catch (error) {
    console.error('Error formatting time range:', error);
    return 'Invalid time range';
  }
};
